import { formatCsvToEuro, sanitizeCSV } from '@hawkindynamics/core-client';
import * as CSV from 'comma-separated-values';
import dayjs from 'dayjs';
import { saveAs as npmSaveAs } from 'file-saver';
import JSZip from 'jszip';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import max from 'lodash/max';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import { calculateStatistics } from '../data/providers/utils/calculateStatistics';
import {
  EZScorePreference,
  IAggregated,
  IAthlete,
  ICSVDefinition,
  IExportGroupedByAthlete,
  IExportGroupedByAthleteAndSession,
  IExportGroupedByAthleteAndSessionAndQueryHash,
  IGroup,
  IMetric,
  ISelectOption,
  ISelectOptionNumber,
  ITag,
  ITeam,
  ITest,
  ITestRepetition,
} from '../data/types';
import { formatDayFromUtc } from './date';
import { roundValue } from './math';
import { safeFilename } from './safeFilename';
import { toMetricList } from './testMetrics';

export type ValidExportDateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY';

/**
 * @description downloads and logs a file
 * @param uri `string | Blob`
 * @param fileDescription  `string`
 * @returns `void`
 */
const saveAs = (uri: string | Blob, fileDescription: string): void => {
  npmSaveAs(uri, fileDescription);
};

/**
 * @description builds a CSV from a given list of tests
 * @param data `ITest[]`
 * @param filename `string`
 * @param averageType `string`
 *
 * @returns `{filename: string, data: CSV}`
 */
export const buildCSV = (
  data: ITest[],
  filename: string,
  dateFormat: ValidExportDateFormat = 'MM/DD/YYYY',
  averageType: string = 'none',
  useDecimalComma: boolean = false
): { filename: string; data: unknown } => {
  const raw = makeCSVData(data, dateFormat, averageType);
  const csv = new CSV(raw, {
    header: true,
  }).encode();
  return {
    filename,
    data: useDecimalComma
      ? formatCsvToEuro(sanitizeCSV(csv))
      : sanitizeCSV(csv),
  };
};

/**
 * @description builds a CSV from a given list of repetitions
 * @param data
 * @param filename
 * @param averageType
 *
 * @returns
 */
export const buildRepetitionCSV = (
  data: ITestRepetition[],
  dateFormat: ValidExportDateFormat = 'MM/DD/YYYY'
): CSV => {
  const raw = makeRepetitionCSVData(data, dateFormat);
  const csv = new CSV(raw, { header: true }).encode();
  return sanitizeCSV(csv);
};

/**
 * @description builds a CSV from a raw data source and converts to a uri
 * @param raw
 * @param opts
 */
// tslint:disable-next-line:no-any
export const makeDataUri = (raw: any, opts: any): string => {
  const csv = new CSV(raw, { ...opts }).encode();
  return encodeURI(
    [
      'data:text/csv;charset=utf-8',
      opts.useDecimalComma
        ? formatCsvToEuro(sanitizeCSV(csv))
        : sanitizeCSV(csv),
    ].join(',')
  );
};

/**
 * @description downloads a single test as csv
 * @param test
 * @param filename
 * @param time
 */
export const downloadSingleTestCSV = (
  test: ITest,
  filename: string,
  time: string = null,
  dateFormat?: ValidExportDateFormat,
  useDecimalComma: boolean = false
): void => {
  const raw = makeCSVData([test], dateFormat);
  const uri = makeDataUri(raw, {
    header: true,
    useDecimalComma,
  });
  filename = filename || 'hd-output-';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `${filename}_${time}.csv`;
  saveAs(uri, fileDescription);
};
/**
 * @description creates and downloads a CSV from a list of tests of the same type
 * @param data `ITest[]`
 * @param filename `string`
 * @param time? `string`
 * @returns void
 */
export const downloadSingleTestTypeCSV = (
  data: ITest[],
  filename: string,
  useDecimalComma: boolean = false,
  time?: string,
  dateFormat?: ValidExportDateFormat
): void => {
  const raw = makeCSVData(data, dateFormat);
  const uri = makeDataUri(raw, { header: true, useDecimalComma });
  filename = filename || 'hd-output-';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `${filename}_${time}.csv`;
  saveAs(uri, fileDescription);
};
/**
 * @description scoreboard download
 * @param sessionArray `ITest[]`
 * @param metricObj `ISelectOption`
 * @param filename `string`
 * @returns `void`
 */
export const downloadScoreboard = (
  sessionArray: ITest[],
  metricObj: ISelectOption,
  filename: string
): void => {
  const raw = makeCSVScoreboardData(sessionArray, metricObj);
  const uri = makeDataUri(raw, { header: true });
  filename = filename || 'hd-scoreboard-output';
  const fileDescription = makeFilename(`${filename}.csv`);
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the force-time curve
 * @param data `number[][]`
 * @param filename `string`
 * @param time `string`
 * @returns void
 */
export const downloadForceCSV = (
  data: number[][],
  filename: string,
  time: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: [
      'Time (s)',
      'Left (N)',
      'Right (N)',
      'Combined (N)',
      'Moments Left',
      'Moments Right',
      'X Left (N)',
      'X Right (N)',
      'X Moments Left',
      'X Moments Right',
      'Y Left (N)',
      'Y Right (N)',
      'Y Moments Left',
      'Y Moments Right',
    ],
    useDecimalComma,
  });
  filename = filename || 'hd-output';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `Force-${filename}-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the sensor data
 * @param data `number[][]`
 * @param filename `string`
 * @param time `string`
 * @returns void
 */
export const downloadSensorsCSV = (
  data: number[][],
  filename: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: [
      'Time (s)',
      'Right Front Left (N)',
      'Right Back Left (N)',
      'Right Back Right (N)',
      'Right Front Right (N)',
      'Left Front Left (N)',
      'Left Back Left (N)',
      'Left Back Right (N)',
      'Left Front Right (N)',
    ],
    useDecimalComma,
  });
  filename = filename || 'hd-output';

  const fileDescription = `Sensors-${filename}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of TruStrength raw data
 * @param data `number[][]`
 * @param filename `string`
 * @param useDecimalComma `boolean`
 * @param ratedOutput `number` - 2 for 2kN, 3 for 3kN load cell
 * @returns void
 */
export const downloadTruStrengthRawCSV = (
  data: number[][],
  filename: string,
  useDecimalComma: boolean = false,
  ratedOutput?: number
): void => {
  const header = ['Time (s)', 'Raw ADC Value'];
  if (ratedOutput) {
    header.push(`Rated Output (${ratedOutput}kN)`);
  }

  const uri = makeDataUri(data, {
    header,
    useDecimalComma,
  });
  filename = filename || 'hd-output';

  const fileDescription = `TruStrength-Raw-${filename}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the sensor data
 * @param data `number[][]`
 * @param filename `string`
 * @param time `string`
 * @returns void
 */
export const downloadCOPCSV = (
  data: Array<Array<number | string>>,
  filename: string,
  useDecimalComma: boolean = false
): void => {
  const sanitizedData = data.map(
    (row: Array<number | string>): Array<number | string> => {
      return row.map((value: number | string): number | string => {
        return value === null ? '--' : value;
      });
    }
  );
  const header = [
    'Time (s)',
    'COP X',
    'COP Y',
    'Left COP X',
    'Left COP Y',
    'Right COP X',
    'Right COP Y',
  ];
  const uri = makeDataUri(sanitizedData, {
    header,
    cast: range(header.length).map((_: number): NumberConstructor => Number),
    useDecimalComma,
  });

  filename = filename || 'hd-output';

  const fileDescription = `COP-${filename}.csv`;
  saveAs(uri.replace(RegExp('NaN', 'g'), ''), fileDescription);
};

/**
 * @description creates and downloads a CSV of the velocity trace for a single test
 * @param data `number[][]`
 * @param filename `string`
 * @param time? `string`
 * @returns void
 */
export const downloadVelocityCSV = (
  data: number[][],
  filename: string,
  time: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: ['Time (s)', 'Velocity (m/s)'],
    useDecimalComma,
  });
  filename = filename || 'hd-output';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `Velocity-${filename}-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the displacement trace for a single test
 * @param data `number[][]`
 * @param filename `string`
 * @param time? `string`
 * @returns void
 */
export const downloadDisplacementCSV = (
  data: number[][],
  filename: string,
  time: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: ['Time (s)', 'Displacement (m)'],
    useDecimalComma,
  });
  filename = filename || 'hd-output';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `Displacement-${filename}-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the power trace for a single test
 * @param data `number[][]`
 * @param filename `string`
 * @param time? `string`
 * @returns void
 */
export const downloadPowerCSV = (
  data: number[][],
  filename: string,
  time: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: ['Time (s)', 'Power (W)'],
    useDecimalComma,
  });
  filename = filename || 'hd-output';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `Power-${filename}-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description creates and downloads a CSV of the RSI for each jump in a multi rebound test
 * @param data `number[][]`
 * @param filename `string`
 * @param time? `string`
 * @returns void
 */
export const downloadRsiCSV = (
  data: string[][],
  filename: string,
  time: string,
  useDecimalComma: boolean = false
): void => {
  const uri = makeDataUri(data, {
    header: ['Jump', 'RSI'],
    useDecimalComma,
  });
  filename = filename || 'hd-output';
  time = (time || Date.now()).toString().replace(new RegExp(/:/g), '-');
  const fileDescription = `RSI-${filename}-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description concatenates athlete name and test type into a file safe name
 * @param athleteName `string`
 * @param testTest `string`
 * @returns `string`
 */
export const makeFilename = (
  athleteName: string,
  testType?: string
): string => {
  let name = `${athleteName}`;
  if (testType) {
    name += `_${testType}`;
  }
  return safeFilename(name);
};

/**
 * @description creates and downloads a zip file of the supplied files
 * @param files `Array<{ filename: string; data: object[] }`
 * @param filename `string`
 * @returns `void`
 */
export const downloadZipFile = (
  files: ICSVDefinition[],
  filename: string
): Promise<void> => {
  const zip = new JSZip();
  files.forEach((file: ICSVDefinition): void => {
    if (file.data.length > 0) {
      zip.file(file.filename, file.data);
    }
  });
  return zip.generateAsync({ type: 'blob' }).then((base: Blob): void => {
    saveAs(base, makeFilename(filename));
  });
};

/**
 * @description calculates and includes the averages
 * @param groupedTests `IExportGroupedByAthleteAndSessionAndQueryHash,`
 * @param averageType `string` optional
 */
const formatWithAverages = (
  groupedTests: IExportGroupedByAthleteAndSessionAndQueryHash,
  averageType: string = 'none'
): IExportGroupedByAthleteAndSessionAndQueryHash => {
  if (averageType === 'none') return groupedTests;
  const iCopy = cloneDeep(groupedTests);
  const athletes = Object.keys(groupedTests);
  const excludeMetaKeys = [
    'athlete',
    'testType',
    'tags',
    'id',
    'sessionId',
    'timestamp',
  ];

  athletes.forEach((athlete: string): void => {
    const sessions = groupedTests[athlete];
    Object.keys(sessions).forEach((session: string): void => {
      const queryHashes = cloneDeep(iCopy[athlete][session]);
      Object.keys(queryHashes).forEach((hash: string): void => {
        const tests = cloneDeep(queryHashes[hash]);
        const pushArr = [];
        const averagableMetrics = Object.keys(cloneDeep(tests[0])).reduce(
          (
            acc: { [metricKey: string]: number },
            curr: string
          ): { [metricKey: string]: number } => {
            const metric = clone(tests[0])[curr];
            if (!metric) return acc;
            if (metric.label && metric.value) {
              acc[curr] = 0;
            }
            return acc;
          },
          {}
        );
        const nonMetrics = Object.keys(cloneDeep(tests[0])).reduce(
          (acc: Partial<ITest>, curr: string): Partial<ITest> => {
            const metric = clone(tests[0])[curr];
            if (
              (!metric || !metric.label || !metric.value) &&
              !excludeMetaKeys.includes(curr)
            ) {
              acc[curr] = curr;
            }
            return acc;
          },
          {}
        );
        const copyTest = cloneDeep(tests[0]);
        const avgTest = { ...copyTest };
        avgTest.isAvgTest = true;
        avgTest.id = 'AVERAGE';
        avgTest.segmentId = 'N/A';
        tests.forEach((test: ITest): void => {
          Object.keys(test).forEach((metricId: string): void => {
            if (
              typeof averagableMetrics[metricId] === 'number' &&
              typeof test[metricId].value === 'number'
            ) {
              averagableMetrics[metricId] =
                averagableMetrics[metricId] + test[metricId].value;
            }
          });
          if (averageType !== 'averagesOnly') {
            pushArr.push(test);
          }
        });
        Object.keys(averagableMetrics).forEach((type: string): void => {
          avgTest[type].value = averagableMetrics[type] / tests.length;
        });
        Object.keys(nonMetrics).forEach((type: string): void => {
          avgTest[type] =
            typeof avgTest[type] !== 'object'
              ? 'N/A'
              : { ...nonMetrics[type], value: null };
        });

        pushArr.push(avgTest);
        const copy = clone(avgTest);
        Object.keys(copy).forEach((key: string): void => {
          copy[key] = ' ';
        });
        pushArr.push(copy);
        if (averageType !== 'averagesOnly') {
          pushArr[0] = cloneDeep(tests[0]);
        }
        iCopy[athlete][session][hash] = pushArr;
      });
    });
  });
  return iCopy;
};

/**
 * @description formats a list of tests into CSV row data in correct order
 * @param data `ITest[]`
 * @param averageType `optional - string`
 * @returns `object[]`
 */
const makeRepetitionCSVData = (
  data: ITestRepetition[],
  dateFormat: ValidExportDateFormat = 'MM/DD/YYYY'
): Array<Record<string, unknown>> => {
  const finalRepetitions = [];
  for (const repetition of data) {
    const finalRepetition: Record<string, unknown> = {};
    if (repetition.id === ' ') {
      finalRepetition.TestId = ' ';
      finalRepetition.RepetitionId = ' ';
      finalRepetition.RepetitionNumber = ' ';
      finalRepetition.Date = ' ';
      finalRepetition.Time = ' ';
      finalRepetition.Name = ' ';
      finalRepetition.Segment = ' ';
      finalRepetition.Position = ' ';
      finalRepetition.Type = ' ';
      finalRepetition.Excluded = ' ';
      finalRepetition.Tags = ' ';
    } else {
      finalRepetition.TestId = repetition.testId;
      finalRepetition.RepetitionId = repetition.id;
      finalRepetition.RepetitionNumber = repetition.repetitionNumber;
      finalRepetition.StartTime = repetition.start;
      finalRepetition.Date = dayjs(repetition.timestamp).format(dateFormat);
      finalRepetition.Time =
        dayjs(repetition.timestamp).format('HH:mm:ss') || null;
      finalRepetition.Name = repetition.athlete.name;
      finalRepetition.Position = repetition.athlete.position;
      finalRepetition.Type = repetition.testType.name;
      finalRepetition.Excluded = repetition.tags.reduce(
        (acc: boolean, curr: ITag): boolean => {
          if (acc) return acc;
          return curr.exclude_aggregates;
        },
        false
      )
        ? 'Yes'
        : '';
      finalRepetition.Tags = repetition.tags
        .map((tag: ITag): string => tag.name)
        .join(',');
    }

    const row = {
      ...finalRepetition,
    };
    sortBy(Object.keys(repetition.metrics), (i: string): number => {
      /// sort metrics
      return repetition.metrics[i]?.order;
    }).forEach(
      (
        metricId: string
      ): {
        [metricLabel: string]: number;
      } => {
        if (!repetition.metrics[metricId] || metricId === 'undefined') return;
        if (
          repetition.metrics[metricId].hasOwnProperty('value') &&
          repetition.metrics[metricId]?.label
        ) {
          row[repetition.metrics[metricId].label] = displayMetric(
            repetition.metrics[metricId]
          );
        }
      },
      {}
    );
    finalRepetitions.push(row);
  }
  return finalRepetitions;
};

/**
 * @description formats a list of tests into CSV row data in correct order
 * @param data `ITest[]`
 * @param averageType `optional - string`
 * @returns `object[]`
 */
const makeCSVData = (
  data: ITest[],
  dateFormat: ValidExportDateFormat = 'MM/DD/YYYY',
  averageType: string = 'none'
): object[] => {
  return flatten(
    unwrapTests(
      formatWithAverages(
        groupByQueryHash(
          groupByAthleteAndSession(
            groupByAthlete(orderByAthleteAndTimestamp(data))
          )
        ),
        averageType
      )
    ).map((test: ITest): object => {
      const finalJump: any = {};

      if (test.id === ' ') {
        finalJump.TestId = ' ';
        finalJump.Date = ' ';
        finalJump.Time = ' ';
        finalJump.Name = ' ';
        finalJump.Segment = ' ';
        finalJump.Position = ' ';
        finalJump.Type = ' ';
        finalJump.Excluded = ' ';
        finalJump.Tags = ' ';
      } else {
        finalJump.TestId = test.id;
        finalJump.Date = dayjs(test.timestamp).format(dateFormat);
        finalJump.Time = test.isAvgTest
          ? 'N/A'
          : dayjs(test.timestamp).format('HH:mm:ss') || null;
        delete test.isAvgTest;
        finalJump.Name = test.athlete.name;
        finalJump.Segment = test.segmentId;
        finalJump.Position = test.athlete.position ?? ' ';
        finalJump.Type = test.testType.name ?? ' ';
        finalJump.Excluded = test.tags.reduce(
          (acc: boolean, curr: ITag): boolean => {
            if (acc) return acc;
            return curr.exclude_aggregates;
          },
          false
        )
          ? 'Yes'
          : '';
        finalJump.Tags = test.tags
          .map((tag: ITag): string => tag.name)
          .join(',');
      }

      const row = {
        ...finalJump,
      };
      sortBy(Object.keys(test), (i: string): number => {
        return test[i]?.order;
      }).forEach(
        (
          metric: string
        ): {
          [metricLabel: string]: number;
        } => {
          if (!test[metric] || metric === 'undefined') return;
          if (test[metric].hasOwnProperty('value') && test[metric]?.label) {
            row[test[metric].label] = displayMetric(test[metric]);
          }
        },
        {}
      );

      return row;
    }, [])
  );
};
/**
 * @description
 * @param metric
 * @returns
 */
const displayMetric = (metric: IMetric): string | number => {
  if (metric.isBoolean) {
    if (metric?.value === 0) {
      return 'No';
    } else {
      return 'Yes';
    }
  } else if (metric?.value !== null) {
    return metric.value;
  } else {
    return 'N/A';
  }
};
/**
 * @description formats the scoreboard data into a CSV
 * @param data `Iest[]`
 * @param metricObj `ISelectOption`
 * @returns `any[]`
 */
const makeCSVScoreboardData = (
  data: ITest[],
  metricObj: ISelectOption
  // tslint:disable-next-line:no-any
): any[] => {
  return data.map((session: ITest): object => {
    const finalSession = {
      TestId: session.id,
      Date: dayjs(session.timestamp).format('MM/DD/YY'),
      Time: dayjs(session.timestamp).format('HH:mm:ss'),
      Name: session.athlete.name,
      Position: session.athlete.position ?? ' ',
      Type: session.testType.name,
      Tags: session.tags.map((tag: ITag): string => tag.name).join(','),
    };
    finalSession[metricObj.label] = session[metricObj.value].value;
    return finalSession;
  }, []);
};

/**
 * @description groups a list of tests by session from an object that contains lists of tests
 * 	keyed by athlete name
 * @param data `{[athleteName: string]: ITest[]}`
 * @returns `IExportGroupedByAthleteAndSession`
 */
const groupByAthleteAndSession = (
  data: IExportGroupedByAthlete
): IExportGroupedByAthleteAndSession => {
  return Object.keys(data).reduce(
    (
      acc: IExportGroupedByAthleteAndSession,
      curr: string
    ): IExportGroupedByAthleteAndSession => {
      acc[curr] = groupBy(data[curr], (item: ITest): number =>
        dayjs(item.timestamp).startOf('day').valueOf()
      );
      return acc;
    },
    {}
  );
};

/**
 * @description orders an array of tests by athlete and then time
 * @param data {ITest[]}
 * @returns {ITest[]}
 */
const orderByAthleteAndTimestamp = (data: ITest[]): ITest[] => {
  return orderBy(
    data,
    [
      (item: ITest): string => {
        return item.athlete.name;
      },
      'timestamp',
    ],
    ['asc', 'desc']
  );
};

/**
 * @description groups tests with the same query hash together in the given object
 * @param data `IExportGroupedByAthleteAndSession`
 * @returns `IExportGroupedByAthleteAndSessionAndQueryHash`
 */
const groupByQueryHash = (
  data: IExportGroupedByAthleteAndSession
): IExportGroupedByAthleteAndSessionAndQueryHash => {
  const copy = { ...data };
  return Object.keys(copy).reduce(
    (
      acc: IExportGroupedByAthleteAndSessionAndQueryHash,
      athleteName: string
    ): IExportGroupedByAthleteAndSessionAndQueryHash => {
      const athleteSessions = { ...copy[athleteName] };
      Object.keys(athleteSessions).forEach((sessionId: string): void => {
        const tests = [].concat(copy[athleteName][sessionId]);
        acc[athleteName] = {
          ...acc[athleteName],
          [sessionId]: groupBy(tests, (item: ITest): string => item.queryHash),
        };
      });
      return acc;
    },
    {}
  );
};

/**
 * @description groups a list of tests by athlete name
 * @param list {ITest[]}
 * @returns {[athleteName: string]: ITest[]}
 */
const groupByAthlete = (list: ITest[]): { [athleteName: string]: ITest[] } => {
  return groupBy(list, (item: ITest): string => {
    return item.athlete.id;
  });
};

/**
 * @description unwraps the sorted tests into a single array of 'rows'
 * @param data `IExportGroupedByAthleteAndSessionAndQueryHash`
 * @returns `ITest[]`
 */
const unwrapTests = (
  data: IExportGroupedByAthleteAndSessionAndQueryHash
): ITest[] => {
  return Object.keys(data).reduce(
    (unwrappedTests: ITest[], athleteName: string): ITest[] => {
      const athletes = Object.keys(data[athleteName]).reduce(
        (athleteTests: ITest[], sessionId: string): ITest[] => {
          const sessions = Object.keys(data[athleteName][sessionId]).reduce(
            (sessionTests: ITest[], queryHash: string): ITest[] => {
              const testsOfSameQueryHash =
                data[athleteName][sessionId][queryHash];
              return [].concat(sessionTests, testsOfSameQueryHash);
            },
            []
          );
          return [].concat(athleteTests, sessions);
        },
        []
      );
      return [].concat(unwrappedTests, athletes);
    },
    []
  );
};

export interface IRosterExport {
  Id: string;
  Name: string;
  Position: string;
  Groups: string;
  LastTested: string;
}

/**
 * @description downloads the roster of athletes in a given team to CSV file
 * @param athletes {IAthlete[]}
 * @param groups {IGroup[]}
 * @param teams {ITeam[]}
 * @returns void
 */
export const downloadRoster = (
  athletes: IAthlete[],
  groups: IGroup[],
  teams: ITeam[]
): void => {
  /// group the athletes by team

  const files = teams.map((team: ITeam): ICSVDefinition => {
    const athletesInTeam = athletes
      .filter((athlete: IAthlete): boolean => athlete.teams?.includes(team.id))
      .map((athlete: IAthlete): IRosterExport => {
        /// swap the group id for the group name
        athlete.groups = sortBy(
          athlete.groups,
          (group: IGroup): string => group.id
        ).reduce((acc: string[], groupId: string): string[] => {
          const group = groups
            .filter((g: IGroup): boolean => {
              /// only include groups that belong to the current team
              return g.teams?.includes(team.id);
            })
            .find((g: IGroup): boolean => g.id === groupId);
          if (!group) return acc;
          return [].concat(acc, [group.name]);
        }, []);
        /// convert to export format
        const maxDate = max(athlete.testedOn);
        return {
          Id: athlete.id,
          Name: athlete.name,
          Position: athlete.position,
          Groups: athlete.groups.join(','),
          LastTested: maxDate ? formatDayFromUtc(maxDate * 1000) : 'Never',
        };
      });
    const csv = new CSV(athletesInTeam, { header: true }).encode();
    return {
      filename: makeFilename(`${team.name}_roster.csv`),
      data: sanitizeCSV(csv),
    };
  });
  downloadZipFile(files, 'hawkindynamics-rosters.zip');
};
/**
 * @description downloads test w/ comparison CSV for given test
 * @param aggregates {IAggregated}
 * @param metrics {IMetric[]}
 * @param filename {String}
 * @param time {String}
 * @param period {string | number}
 * @param startDate {ISelectOptionNumber}
 * @param endDate {ISelectOptionNumber}
 * @returns void
 */
export const makeTestWithComparisonCSV = (
  aggregates: IAggregated,
  test: ITest,
  filename: string,
  time: string,
  useDecimalComma: boolean = false,
  period: string | number,
  startDate?: ISelectOptionNumber,
  endDate?: ISelectOptionNumber,
  scale?: EZScorePreference
): void => {
  const { aggregate } = aggregates;

  const periodColumn =
    period === 'Custom'
      ? `${startDate.label}-${endDate.label} Mean`
      : period === 'All'
      ? 'All Time Mean'
      : `${period} Mean`;
  const header = ['Metric', 'Result', periodColumn, 'Change %', 'Z-Score'];

  const statistics = calculateStatistics(test, aggregate?.data, scale);
  const rows = toMetricList(test).map(
    (metric: IMetric): Array<string | number> => {
      const mean = roundValue(
        statistics?.[metric.id]?.mean,
        metric.roundingPrecision
      );
      const change = roundValue(statistics?.[metric.id]?.change, 1);
      const zscore = statistics?.[metric.id]?.zscore;
      const value = roundValue(metric.value, metric.roundingPrecision);
      return [
        metric.label,
        value,
        mean ?? 'NA',
        change ?? 'NA',
        zscore ?? 'NA',
      ];
    }
  );
  const uri = makeDataUri(rows, {
    header,
    useDecimalComma,
  });
  const fileDescription = `${filename}-Comparison-${time}.csv`;
  saveAs(uri, fileDescription);
};

/**
 * @description converts a list of tags to a file name safe string
 * @param tags
 * @returns
 */
export const concatTagNames = (tags: ITag[]): string => {
  let tagNames = tags
    .map((t: ITag): string => t.name.replace(/(\s+)?-(\s+)?/, ' '))
    .join(' ');
  if (tagNames.length > 0) {
    tagNames = ` ${tagNames}`;
  }
  return tagNames;
};
